import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import type { Entity } from "model/OrganizationalStructure";
import { getEntityDisplayName } from "./organizationSettings.logic";
import {
  EntityTab,
  EntityTabButton,
  EntityTabEditAction,
  EntityTabsList,
  EntityTabsViewport,
  IconButton,
} from "./organizationSettings.styled";

interface EntityTabsProps {
  readonly entities: readonly Entity[];
  readonly selectedEntityId: number;
  readonly onEdit: (entity: Entity) => void;
  readonly onSelect: (entityId: number) => void;
}

export const EntityTabs = (props: EntityTabsProps): JSX.Element => {
  const { t } = useTranslation();
  const activeTabRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    activeTabRef.current?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
  }, [props.selectedEntityId]);

  return (
    <EntityTabsViewport>
      <EntityTabsList
        role="group"
        aria-label={t("OrganizationSettings.Entities")}
      >
        {props.entities.map((entity) => {
          const isActive = entity.id === props.selectedEntityId;
          const displayName = getEntityDisplayName({
            name: entity.name,
            unhandledName: t("OrganizationSettings.Unhandled"),
          });
          return (
            <EntityTab
              key={entity.id}
              ref={isActive ? activeTabRef : undefined}
              $isActive={isActive}
            >
              <EntityTabButton
                type="button"
                aria-pressed={isActive}
                $isActive={isActive}
                onClick={() => props.onSelect(entity.id)}
              >
                {displayName}
              </EntityTabButton>
              {isActive && (
                <EntityTabEditAction>
                  <TooltipHover text={t("OrganizationSettings.EntitySettings")}>
                    <IconButton
                      type="button"
                      aria-label={t("OrganizationSettings.EditEntity", {
                        name: displayName,
                      })}
                      onClick={() => props.onEdit(entity)}
                    >
                      <CustomIcon
                        name="v2/pen"
                        size="18px"
                        color={ColorV2.Blue1}
                      />
                    </IconButton>
                  </TooltipHover>
                </EntityTabEditAction>
              )}
            </EntityTab>
          );
        })}
      </EntityTabsList>
    </EntityTabsViewport>
  );
};
